"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Bookmark, Heart, RefreshCw, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useLikeToggle } from "@/features/likes/useLikeToggle";
import { useWatchlistToggle } from "@/features/watchlist/useWatchlistToggle";
import type { NftView } from "./nftView";

/**
 * Like, save, refresh, share — the legacy page's four controls, the same
 * hooks and messages (`useLikeToggle` / `useWatchlistToggle` on "nft"),
 * refresh as `router.refresh()` with its toast, share through the system
 * sheet, else the clipboard, else the old copy command.
 */
export default function NftActs({
  v,
  dark = false
}: {
  v: NftView;
  dark?: boolean;
}) {
  const isFa = v.lang === "fa";
  const t = (fa: string, en: string) => isFa ? fa : en;
  const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
  const router = useRouter();
  const [refreshing, setRefreshing] = useState(false);
  const like = useLikeToggle({
    modelType: "nft",
    modelId: v.id,
    initialLiked: v.liked,
    initialCount: v.likes,
    errorMessage: t("خطا در ثبت پسند", "Failed to update like")
  });
  const save = useWatchlistToggle({
    modelType: "nft",
    modelId: v.id,
    initialWatchlisted: v.watchlisted,
    successMessage: {
      added: t("به واچ‌لیست اضافه شد", "Added to watchlist"),
      removed: t("از واچ‌لیست حذف شد", "Removed from watchlist")
    },
    errorMessage: t("خطا در ثبت واچ‌لیست", "Failed to update watchlist")
  });
  const refresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    router.refresh();
    toast.success(t("اطلاعات به‌روزرسانی شد", "Item refreshed"));
    setTimeout(() => setRefreshing(false), 800);
  };
  const share = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({
          title: document.title,
          url
        });
        return;
      }
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        toast.success(t("لینک کپی شد", "Link copied!"));
        return;
      }
      const textarea = document.createElement("textarea");
      textarea.value = url;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      toast.success(t("لینک کپی شد", "Link copied!"));
    } catch {
      // The share sheet was cancelled — nothing to report.
    }
  };
  return <span className={dark ? "ap-acts ap-acts--dark" : "ap-acts"}>
      <button type="button" className="ap-act v2-focus" data-on={like.liked || undefined} onClick={like.toggle} disabled={like.isPending} aria-pressed={like.liked} aria-label={t("پسندیدن", "Like")}>
        <Heart aria-hidden /><b>{nf.format(like.count)}</b>
      </button>
      <button type="button" className="ap-act v2-focus" data-on={save.watchlisted || undefined} onClick={save.toggle} disabled={save.isPending} aria-pressed={save.watchlisted} aria-label={t("افزودن به واچ‌لیست", "Add to watchlist")}>
        <Bookmark aria-hidden />
      </button>
      <button type="button" className="ap-act v2-focus" onClick={refresh} disabled={refreshing} aria-busy={refreshing || undefined} aria-label={refreshing ? t("در حال به‌روزرسانی", "Refreshing") : t("به‌روزرسانی", "Refresh")}>
        <RefreshCw aria-hidden className={refreshing ? "ns-spin" : undefined} />
      </button>
      <button type="button" className="ap-act v2-focus" onClick={share} aria-label={t("اشتراک‌گذاری", "Share")}>
        <Share2 aria-hidden />
      </button>
    </span>;
}